(() => {
  if (customElements.get('hamilton-home-hero')) return;
  class HamiltonHomeHero extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.track = this.querySelector('.hamilton-home-hero__track');
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.pauseButton = this.querySelector('[data-hero-pause]');
      this.index = 0;
      this.trackWidth = this.track.clientWidth;
      this.userPaused = false;
      this.navigationPaused = false;
      this.inView = this.isInViewport();
      this.hovered = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      const on = (target, event, handler) => target?.addEventListener(event, handler, {signal: this.abort.signal});
      on(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-hero-pause')) {
          this.userPaused = !this.userPaused;
          if (!this.userPaused) this.navigationPaused = false;
          this.sync();
        } else if (button.hasAttribute('data-hero-dot')) this.go(Number(button.dataset.heroDot), true);
        else if (button.hasAttribute('data-hero-prev')) this.go(this.index - 1, true);
        else if (button.hasAttribute('data-hero-next')) this.go(this.index + 1, true);
      });
      on(this.track, 'keydown', event => {
        if (event.target !== this.track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        this.go(event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 : this.index + (event.key === 'ArrowLeft' ? -1 : 1), true);
      });
      on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          if (this.track.clientWidth !== this.trackWidth) return;
          const next = Math.round(this.track.scrollLeft / this.trackWidth);
          if (next !== this.index && this.slides[next]) { this.index = next; this.sync(); }
        });
      });
      on(this.track, 'pointerdown', () => { this.navigationPaused = true; this.sync(); });
      on(this, 'mouseenter', () => { this.hovered = true; this.sync(); });
      on(this, 'mouseleave', () => { this.hovered = false; this.sync(); });
      on(this, 'focusin', () => this.sync());
      on(this, 'focusout', () => queueMicrotask(() => { if (this.isConnected) this.sync(); }));
      const refreshVisibility = () => { this.inView = this.isInViewport(); this.sync(); };
      on(document, 'visibilitychange', refreshVisibility);
      on(window, 'pageshow', refreshVisibility);
      on(window, 'load', refreshVisibility);
      on(this.reduced, 'change', () => this.sync());
      on(this.mobile, 'change', () => this.sync());
      on(document, 'shopify:block:select', event => {
        const selected = this.slides.indexOf(event.target.closest('[data-hero-slide]'));
        if (selected >= 0) this.go(selected, true);
      });
      this.slides.forEach(slide => slide.querySelectorAll('video').forEach(video => {
        on(video, 'ended', () => { if (slide === this.slides[this.index] && this.canAdvance()) this.go(this.index + 1); });
        on(video, 'error', () => this.schedule());
        for (const event of ['loadeddata', 'canplay']) on(video, event, () => {
          if (slide === this.slides[this.index]) this.sync();
        });
      }));
      this.observer = new IntersectionObserver(refreshVisibility, {threshold:0});
      this.observer.observe(this);
      this.resize = new ResizeObserver(() => {
        cancelAnimationFrame(this.frame);
        this.trackWidth = this.track.clientWidth;
        this.track.scrollLeft = this.index * this.trackWidth;
        refreshVisibility();
      });
      this.resize.observe(this.track);
      this.sync();
    }
    isInViewport() {
      const rect = this.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < (window.innerHeight || document.documentElement.clientHeight);
    }
    canPlay() { return this.inView && !document.hidden && !this.userPaused && !this.reduced.matches; }
    canAdvance() { return this.canPlay() && this.dataset.autoplay === 'true' && !this.navigationPaused && this.slides.length > 1 && !this.hovered && !this.contains(document.activeElement) && !window.Shopify?.designMode; }
    schedule() {
      clearTimeout(this.timer);
      if (this.canAdvance()) this.timer = setTimeout(() => this.go(this.index + 1), Math.max(3000, Number(this.dataset.interval) || 7000));
    }
    go(index, manual = false) {
      if (!this.slides.length) return;
      if (manual) this.navigationPaused = true;
      this.index = (index + this.slides.length) % this.slides.length;
      // Instant snap avoids smooth-scroll announcements and intermediate video playback.
      this.track.scrollLeft = this.index * this.track.clientWidth;
      this.sync();
    }
    sync() {
      clearTimeout(this.timer);
      let waiting = false;
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.querySelectorAll('video').forEach(video => {
          const visible = this.mobile.matches ? video.classList.contains('hamilton-home-hero__mobile') : video.classList.contains('hamilton-home-hero__desktop');
          const playing = active && visible && this.canPlay();
          video.autoplay = playing;
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.loop = !(slide.dataset.wait === 'true' && this.canAdvance());
          if (playing) {
            waiting ||= !video.loop;
            if (video.ended) video.currentTime = 0;
            video.preload = 'auto';
            if (video.paused) video.play()?.catch(() => this.schedule());
          } else video.pause();
        });
      });
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.index)));
      const hasVideo = this.slides.some(slide => slide.querySelector('video'));
      this.pauseButton.hidden = !hasVideo && (this.dataset.autoplay !== 'true' || this.slides.length < 2);
      const paused = this.userPaused || this.reduced.matches;
      this.pauseButton.textContent = paused ? '▷' : 'Ⅱ';
      this.pauseButton.setAttribute('aria-label', paused ? 'Reanudar movimiento' : 'Pausar movimiento');
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.querySelector('[data-hero-status]').textContent = `Banner ${this.index + 1} de ${this.slides.length}`;
      if (!waiting) this.schedule();
    }
    disconnectedCallback() {
      clearTimeout(this.timer); cancelAnimationFrame(this.frame);
      this.abort?.abort(); this.observer?.disconnect(); this.resize?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  }
  customElements.define('hamilton-home-hero', HamiltonHomeHero);
})();
